import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, type UserMatchStats } from '../api-client.js';
import { useAuthStore, type AuthUser } from '../auth/auth-store.js';
import { RecordsPanel } from '../components/goldenballs/RecordsPanel.js';
import { NotificationToggle } from '../notifications/NotificationToggle.js';

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; me: AuthUser }
  | { kind: 'error'; message: string };

/**
 * Renders the signed-in user fetched from the server (not the cached
 * store copy) to prove the JWT is accepted end-to-end. Display name
 * is editable in place — the change pushes back through the auth
 * store so the AppShell header re-renders without a session reset.
 *
 * Below the account block, the page doubles as a personal scoreboard:
 * W/L/D tiles + per-game breakdown, with a link out to the full
 * public leaderboard.
 */
export default function MePage() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [stats, setStats] = useState<UserMatchStats | null>(null);
  const patchUser = useAuthStore((s) => s.patchUser);

  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((me) => {
        if (cancelled) return;
        setState({ kind: 'ready', me });
        api
          .getUserStats(me.id)
          .then((s) => {
            if (!cancelled) setStats(s);
          })
          .catch(() => {
            // Stats are best-effort — a failure here shouldn't
            // tank the account page.
          });
      })
      .catch((error: Error) => {
        if (!cancelled) {
          const message = error instanceof ApiError ? error.message : error.message;
          setState({ kind: 'error', message });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function onSavedDisplayName(newName: string) {
    setState((prev) =>
      prev.kind === 'ready' ? { kind: 'ready', me: { ...prev.me, displayName: newName } } : prev,
    );
    patchUser({ displayName: newName });
  }

  return (
    <section className="space-y-6">
      <h2 className="font-display text-lg font-semibold uppercase tracking-wide text-ec-face">Your account</h2>
      {state.kind === 'loading' && <p className="text-ec-bone">Loading…</p>}
      {state.kind === 'error' && (
        <p className="text-ec-oxblood-2">Failed to load: {state.message}</p>
      )}
      {state.kind === 'ready' && (
        <>
          <dl className="ec-surface grid max-w-md gap-3 p-4 text-sm">
            <DisplayNameRow current={state.me.displayName} onSaved={onSavedDisplayName} />
            <Row label="Email" value={state.me.email} />
            <Row label="User ID" value={state.me.id} mono />
          </dl>
          <StatsBlock stats={stats} />
          <div className="max-w-2xl">
            <RecordsPanel userId={state.me.id} />
          </div>
          <div className="space-y-2">
            <h3 className="font-display text-sm font-medium uppercase tracking-wide text-ec-bone">Notifications</h3>
            <NotificationToggle />
          </div>
        </>
      )}
    </section>
  );
}

function DisplayNameRow({
  current,
  onSaved,
}: {
  current: string;
  onSaved: (newName: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(current);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Reset draft when the source value changes (e.g. after another tab
  // pushed a different name).
  useEffect(() => {
    setValue(current);
  }, [current]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = value.trim();
    if (trimmed.length < 2) {
      setError('Display name must be at least 2 characters.');
      return;
    }
    if (trimmed.length > 64) {
      setError('Display name must be 64 characters or fewer.');
      return;
    }
    if (trimmed === current) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await api.updateProfile({ displayName: trimmed });
      onSaved(updated.displayName);
      setEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <div className="flex items-baseline justify-between gap-4">
        <dt className="text-ec-bone-dim">Display name</dt>
        <dd className="flex items-baseline gap-3">
          <span className="text-ec-face">{current}</span>
          <button
            type="button"
            onClick={() => {
              setEditing(true);
              setValue(current);
              setError(null);
            }}
            className="text-xs text-ec-brass hover:underline"
          >
            Edit
          </button>
        </dd>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-2">
      <div className="flex items-baseline justify-between gap-4">
        <dt className="text-ec-bone-dim">Display name</dt>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          minLength={2}
          maxLength={64}
          autoFocus
          className="block min-h-[40px] flex-1 rounded-md border border-ec-line bg-ec-ground px-3 py-1.5 text-base text-ec-face focus:border-ec-brass focus:outline-none focus:ring-1 focus:ring-ec-brass"
        />
        <button
          type="submit"
          disabled={saving}
          className="ec-btn ec-btn--primary !py-1.5 text-sm"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setError(null);
          }}
          disabled={saving}
          className="ec-btn !py-1.5 text-sm"
        >
          Cancel
        </button>
      </div>
      {error && (
        <p role="alert" className="text-xs text-ec-oxblood-2">
          {error}
        </p>
      )}
    </form>
  );
}

function StatsBlock({ stats }: { stats: UserMatchStats | null }) {
  if (!stats) {
    return <p className="text-sm text-ec-bone-dim">Loading stats…</p>;
  }
  if (stats.total === 0) {
    return (
      <div className="ec-surface p-4 font-serif text-sm text-ec-bone">
        No completed matches yet.{' '}
        <Link to="/" className="text-ec-brass hover:underline">
          Start one
        </Link>
        .
      </div>
    );
  }
  const winRate = stats.total > 0 ? Math.round((stats.wins / stats.total) * 100) : 0;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline gap-3">
        <h3 className="font-display text-sm font-medium uppercase tracking-wide text-ec-bone">Match record</h3>
        <Link to="/leaderboard" className="text-xs text-ec-brass hover:underline">
          View full leaderboard →
        </Link>
      </div>
      <div className="grid max-w-md grid-cols-4 gap-2">
        <Stat label="Wins" value={stats.wins} color="text-ec-go" />
        <Stat label="Losses" value={stats.losses} color="text-ec-oxblood-2" />
        <Stat label="Draws" value={stats.draws} color="text-ec-bone" />
        <Stat label="Win %" value={`${winRate}%`} color="text-ec-face" />
      </div>
      {stats.byGame.length > 0 && (
        <table className="ec-surface max-w-md overflow-hidden text-xs">
          <thead className="bg-ec-panel-2 font-display uppercase tracking-wider text-ec-bone">
            <tr>
              <th className="px-3 py-1.5 text-left">Game</th>
              <th className="px-3 py-1.5 text-right">W</th>
              <th className="px-3 py-1.5 text-right">L</th>
              <th className="px-3 py-1.5 text-right">D</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ec-line bg-ec-ground">
            {stats.byGame.map((row) => (
              <tr key={row.gameKey}>
                <td className="px-3 py-1.5 font-mono text-ec-bone">{row.gameKey}</td>
                <td className="px-3 py-1.5 text-right font-display tabular-nums text-ec-go">
                  {row.wins}
                </td>
                <td className="px-3 py-1.5 text-right font-display tabular-nums text-ec-oxblood-2">
                  {row.losses}
                </td>
                <td className="px-3 py-1.5 text-right font-display tabular-nums text-ec-bone">
                  {row.draws}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  color,
}: {
  label: string;
  value: number | string;
  color: string;
}) {
  return (
    <div className="ec-surface p-2 text-center">
      <div className={`font-display text-lg font-semibold tabular-nums ${color}`}>{value}</div>
      <div className="font-display text-[10px] uppercase tracking-wider text-ec-bone-dim">{label}</div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-ec-bone-dim">{label}</dt>
      <dd className={mono ? 'font-mono text-ec-bone' : 'text-ec-face'}>{value}</dd>
    </div>
  );
}
